import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../App';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

export default function SourceDocViewer({ dealId, fileId, sheet, highlightCell, onClose }) {
  const [data, setData] = useState(null);
  const [activeSheet, setActiveSheet] = useState(sheet || '');
  const [loading, setLoading] = useState(true);
  const highlightRef = useRef(null);

  useEffect(() => {
    loadPreview(sheet);
  }, [dealId, fileId]);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [data]);

  const loadPreview = async (sheetName) => {
    setLoading(true);
    const token = localStorage.getItem('cmbs_token');
    const params = new URLSearchParams();
    if (sheetName) params.set('sheet', sheetName);
    if (highlightCell) params.set('highlightCell', highlightCell);
    const res = await fetch(`${API_BASE}/upload/${dealId}/files/${fileId}/preview?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then((r) => r.json());
    if (res.success) {
      setData(res.data);
      setActiveSheet(res.data.activeSheet);
    }
    setLoading(false);
  };

  const handleSheetClick = (name) => {
    setActiveSheet(name);
    loadPreview(name);
  };

  // Parse highlight cell ref (e.g. "B10" -> col "B", row 10)
  let hlCol = null, hlRow = null;
  if (highlightCell) {
    const match = highlightCell.match(/^([A-Z]+)(\d+)$/i);
    if (match) { hlCol = match[1].toUpperCase(); hlRow = parseInt(match[2]); }
  }

  // Get all column letters from the data
  const allCols = [];
  if (data && data.rows.length > 0) {
    const colSet = new Set();
    for (const row of data.rows) {
      for (const cell of row.cells) colSet.add(cell.col);
    }
    const sorted = [...colSet].sort((a, b) => a.charCodeAt(0) - b.charCodeAt(0));
    allCols.push(...sorted);
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ width: '90vw', maxWidth: 1400, height: '85vh', background: '#fff', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', background: COLORS.navy, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <span style={{ fontFamily: 'Georgia, serif', color: COLORS.gold, fontSize: 16 }}>Source: </span>
            <span style={{ color: '#fff', fontSize: 14 }}>{data?.fileName || 'Loading...'}</span>
            {highlightCell && <span style={{ color: COLORS.gold, fontSize: 13, marginLeft: 12 }}>Cell: {highlightCell}</span>}
          </div>
          <button onClick={onClose}
            style={{ width: 36, height: 36, borderRadius: '50%', background: COLORS.navyLight, border: 'none', cursor: 'pointer', color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            X
          </button>
        </div>

        {/* Sheet tabs */}
        {data && data.sheets.length > 1 && (
          <div style={{ display: 'flex', gap: 0, background: '#F0F2F5', padding: '0 12px', flexShrink: 0, borderBottom: `1px solid ${COLORS.border}` }}>
            {data.sheets.map((name) => (
              <button key={name} onClick={() => handleSheetClick(name)}
                style={{
                  padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  borderRadius: '6px 6px 0 0', marginRight: 2,
                  background: name === activeSheet ? COLORS.navy : 'transparent',
                  color: name === activeSheet ? COLORS.gold : COLORS.text,
                }}>
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>Loading spreadsheet...</div>
          ) : !data || data.rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>No data to display</div>
          ) : (
            <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <th style={{ ...thStyle, width: 50, minWidth: 50, position: 'sticky', left: 0, zIndex: 3, background: COLORS.navy }}>#</th>
                  {allCols.map((col) => (
                    <th key={col} style={{ ...thStyle, minWidth: 120, maxWidth: 300 }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => {
                  const isHighlightRow = hlRow === row.rowNum;
                  const cellMap = {};
                  for (const c of row.cells) cellMap[c.col] = c;

                  return (
                    <tr key={row.rowNum} style={{ background: isHighlightRow ? '#FFFDE7' : undefined }}>
                      <td style={{ ...rowNumStyle, position: 'sticky', left: 0, zIndex: 1 }}>{row.rowNum}</td>
                      {allCols.map((col) => {
                        const cell = cellMap[col];
                        const isHL = isHighlightRow && hlCol === col;
                        const val = cell ? cell.formatted : '';
                        const isNum = cell && typeof cell.value === 'number';
                        const isHeader = cell && cell.isHeader;

                        return (
                          <td
                            key={col}
                            ref={isHL ? highlightRef : undefined}
                            style={{
                              padding: '6px 10px',
                              border: isHL ? '3px solid #C9A84C' : '1px solid #E2E6EC',
                              background: isHL ? '#FFF176' : isHeader ? '#E3F2FD' : undefined,
                              textAlign: isNum ? 'right' : 'left',
                              fontWeight: isHeader ? 700 : 400,
                              fontFamily: isNum ? 'monospace' : 'Calibri, Arial, sans-serif',
                              whiteSpace: 'nowrap',
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              position: 'relative',
                            }}
                          >
                            {val}
                            {isHL && (
                              <div style={{
                                position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)',
                                background: '#333', color: '#fff', padding: '2px 8px', borderRadius: 4,
                                fontSize: 10, whiteSpace: 'nowrap', zIndex: 5,
                              }}>
                                Source: {highlightCell}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  padding: '8px 10px',
  background: '#0F1B2D',
  color: '#fff',
  fontWeight: 600,
  fontSize: 12,
  textAlign: 'center',
  border: '1px solid #1A2A42',
  whiteSpace: 'nowrap',
};

const rowNumStyle = {
  padding: '6px 8px',
  background: '#F5F5F5',
  color: '#999',
  fontSize: 11,
  textAlign: 'center',
  border: '1px solid #E2E6EC',
  fontWeight: 600,
};
